/**
 * Skeleton arrows — visible projectiles that travel, collide with
 * blocks and hit the player on proximity. Replaces the old invisible
 * instant damage at range (players lost health "out of nowhere").
 */

import * as THREE from "three";
import { isBlockSolid, type WorldBlockAccess } from "@craftgaming/shared";
import type { MobShootEvent } from "./mob-system";

const ARROW_SPEED = 18; // blocks/s — visible and dodgeable
const ARROW_LIFETIME = 3; // seconds
const ARROW_GRAVITY = 4; // slight arc
const PLAYER_HIT_RADIUS = 0.9;

type Arrow = {
  mesh: THREE.Mesh;
  vx: number;
  vy: number;
  vz: number;
  damage: number;
  age: number;
};

export type ArrowHit = {
  damage: number;
  knockbackDirX: number;
  knockbackDirZ: number;
};

export type MobArrows = {
  spawn: (event: MobShootEvent) => void;
  /** Advances arrows; returns hits on the player (center at chest height). */
  update: (dt: number, playerX: number, playerY: number, playerZ: number) => ArrowHit[];
  dispose: () => void;
};

export function createMobArrows(scene: THREE.Scene, worldAccess: WorldBlockAccess): MobArrows {
  const arrows: Arrow[] = [];
  const geometry = new THREE.BoxGeometry(0.06, 0.06, 0.55);
  const material = new THREE.MeshBasicMaterial({ color: 0x8b7355 });
  const tipMaterial = new THREE.MeshBasicMaterial({ color: 0xcccccc });
  const tipGeometry = new THREE.BoxGeometry(0.09, 0.09, 0.1);

  function spawn(event: MobShootEvent) {
    const mesh = new THREE.Mesh(geometry, material);
    const tip = new THREE.Mesh(tipGeometry, tipMaterial);
    tip.position.z = -0.3;
    mesh.add(tip);

    mesh.position.set(event.fromX, event.fromY, event.fromZ);
    scene.add(mesh);

    arrows.push({
      mesh,
      vx: event.dirX * ARROW_SPEED,
      vy: event.dirY * ARROW_SPEED,
      vz: event.dirZ * ARROW_SPEED,
      damage: event.damage,
      age: 0,
    });
  }

  function removeArrow(index: number) {
    const arrow = arrows[index];
    scene.remove(arrow.mesh);
    arrows.splice(index, 1);
  }

  function update(dt: number, playerX: number, playerY: number, playerZ: number): ArrowHit[] {
    const hits: ArrowHit[] = [];
    const chestY = playerY + 1.0;

    for (let i = arrows.length - 1; i >= 0; i--) {
      const arrow = arrows[i];
      arrow.age += dt;
      if (arrow.age > ARROW_LIFETIME) {
        removeArrow(i);
        continue;
      }

      arrow.vy -= ARROW_GRAVITY * dt;
      const nx = arrow.mesh.position.x + arrow.vx * dt;
      const ny = arrow.mesh.position.y + arrow.vy * dt;
      const nz = arrow.mesh.position.z + arrow.vz * dt;

      // Block collision — arrows stop at walls (cover works!)
      const block = worldAccess.getBlockAtWorld(Math.floor(nx), Math.floor(ny), Math.floor(nz));
      if (isBlockSolid(block)) {
        removeArrow(i);
        continue;
      }

      arrow.mesh.position.set(nx, ny, nz);
      // Point along velocity
      arrow.mesh.lookAt(nx + arrow.vx, ny + arrow.vy, nz + arrow.vz);

      // Player hit
      const dx = nx - playerX;
      const dy = ny - chestY;
      const dz = nz - playerZ;
      if (dx * dx + dy * dy + dz * dz < PLAYER_HIT_RADIUS * PLAYER_HIT_RADIUS) {
        const horizontal = Math.max(0.001, Math.sqrt(arrow.vx * arrow.vx + arrow.vz * arrow.vz));
        hits.push({
          damage: arrow.damage,
          knockbackDirX: arrow.vx / horizontal,
          knockbackDirZ: arrow.vz / horizontal,
        });
        removeArrow(i);
      }
    }

    return hits;
  }

  function dispose() {
    for (const arrow of arrows) scene.remove(arrow.mesh);
    arrows.length = 0;
    geometry.dispose();
    tipGeometry.dispose();
    material.dispose();
    tipMaterial.dispose();
  }

  return { spawn, update, dispose };
}
